import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseChart } from './BaseChart.js';
import { formatNumber } from '../../utils/dataFormatters.js';
import { generateChartAriaLabel } from '../../utils/accessibility.js';
import type { RFMChartProps } from '../../types/analytics.types.js';

/**
 * Production-grade RFM chart with proper segmentation visualization and accessibility
 */
export const RFMChart = memo<RFMChartProps>(
  ({
    data,
    isLoading,
    error,
    emptyMessage,
    emptyHint,
    title,
    subtitle,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
  }) => {
    const { t } = useTranslation('admin');
    const resolvedTitle = title ?? t('analytics.charts.rfmTitle');
    const resolvedSubtitle = subtitle ?? t('analytics.charts.rfmSubtitle');

    const chartAriaLabel = useMemo(
      () => ariaLabel || generateChartAriaLabel(resolvedTitle, data.length, resolvedSubtitle),
      [ariaLabel, resolvedTitle, data.length, resolvedSubtitle]
    );

    // Group by RFM segments
    const segments = useMemo(() => {
      const grouped: Record<string, number> = {};
      data.forEach((bucket) => {
        const key = `${bucket.recency}-${bucket.frequency}-${bucket.monetary}`;
        grouped[key] = (grouped[key] ?? 0) + bucket.count;
      });
      return grouped;
    }, [data]);

    return (
      <BaseChart
        isLoading={isLoading}
        error={error}
        emptyMessage={emptyMessage}
        emptyHint={emptyHint}
        title={resolvedTitle}
        subtitle={resolvedSubtitle}
        aria-label={chartAriaLabel}
        aria-describedby={ariaDescribedBy}
      >
        {data.length > 0 ? (
          <div className="flex flex-col gap-5" role="img" aria-label={chartAriaLabel}>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] gap-3 max-md:grid-cols-[repeat(auto-fill,minmax(80px,1fr))] max-md:gap-2 max-[480px]:grid-cols-[repeat(auto-fill,minmax(70px,1fr))]">
              {Object.entries(segments).map(([segment, count]) => {
                const [recency, frequency, monetary] = segment.split('-');
                const intensity = Math.min(count / 10, 1); // Normalize for color intensity
                return (
                  <div
                    key={segment}
                    className="aspect-square border-2 border-[var(--color-an-border)] rounded-lg flex flex-col items-center justify-center gap-1 cursor-pointer transition-[transform,box-shadow] p-3 hover:scale-105 hover:shadow-md hover:border-[var(--color-an-primary)] focus:outline focus:outline-[3px] focus:outline-[var(--color-an-primary)] focus:outline-offset-2 max-md:min-w-11 max-md:min-h-11"
                    style={{
                      background: `linear-gradient(135deg, color-mix(in srgb, var(--color-an-primary) ${Math.round(intensity * 80)}%, transparent) 0%, color-mix(in srgb, var(--color-an-chart-5, var(--color-an-primary)) ${Math.round(intensity * 60)}%, transparent) 100%)`,
                    }}
                    role="group"
                    aria-label={t('analytics.charts.rfmCellAria', {
                      segment: `${recency}-${frequency}-${monetary}`,
                      count: formatNumber(count),
                    })}
                  >
                    <div className="text-[11px] font-bold text-[var(--color-an-text)] text-center tracking-wide max-md:text-[10px]">
                      {recency}-{frequency}-{monetary}
                    </div>
                    <div className="text-base font-bold text-[var(--color-an-primary)] max-md:text-sm">{formatNumber(count)}</div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-col gap-2 p-4 bg-[var(--color-an-surface-muted)] rounded-lg border border-[var(--color-an-border)]">
              <div className="flex gap-3 text-[13px]">
                <span className="font-bold text-[var(--color-an-text)] min-w-20">{t('analytics.charts.rfmRecencyLabel')}</span>
                <span>{t('analytics.charts.rfmRecencyScale')}</span>
              </div>
              <div className="flex gap-3 text-[13px]">
                <span className="font-bold text-[var(--color-an-text)] min-w-20">{t('analytics.charts.rfmFrequencyLabel')}</span>
                <span>{t('analytics.charts.rfmFrequencyScale')}</span>
              </div>
              <div className="flex gap-3 text-[13px]">
                <span className="font-bold text-[var(--color-an-text)] min-w-20">{t('analytics.charts.rfmMonetaryLabel')}</span>
                <span>{t('analytics.charts.rfmMonetaryScale')}</span>
              </div>
            </div>
          </div>
        ) : null}
      </BaseChart>
    );
  }
);

RFMChart.displayName = 'RFMChart';

